import React, { useState, useEffect, useRef } from 'react';
import { useDaggerheartData } from '../../../context/DataContext';
import DescriptionPromptsHelper from '../DescriptionPromptsHelper';
import BackgroundQuestionsHelper from '../BackgroundQuestionsHelper';

/**
 * DetailsSection - Character name, pronouns, experiences, description, background
 *
 * Uses debounced updates to prevent excessive Firestore writes.
 * Local state provides immediate UI feedback while delaying database writes by 500ms.
 */
function DetailsSection({ character, onUpdate }) {
  const allData = useDaggerheartData();

  // Local state for immediate UI updates
  const [localName, setLocalName] = useState(character.name || '');
  const [localPronouns, setLocalPronouns] = useState(character.pronouns || '');
  const [localExperiences, setLocalExperiences] = useState(character.experiences || ['', '']);
  const [localDescription, setLocalDescription] = useState(character.description || '');
  const [localBackground, setLocalBackground] = useState(character.background || '');
  const [localBackgroundAnswers, setLocalBackgroundAnswers] = useState(character.backgroundAnswers || {});

  // Discrete description variables for AI image generation
  const [localAppearance, setLocalAppearance] = useState(character.appearance || {
    clothes: '',
    eyes: '',
    body: '',
    skin: '',
    attitude: ''
  });

  // Combined appearance text (single string built from chips)
  const [localAppearanceText, setLocalAppearanceText] = useState(character.appearanceText || '');

  // Get class-specific prompts
  const classGuidance = character.class && allData?.classGuidance?.[character.class];
  const descriptionPrompts = classGuidance?.descriptionPrompts;
  const backgroundQuestions = classGuidance?.backgroundQuestions || [
    "What event in your past shaped who you are today?",
    "What do you value most in life?",
    "What is your greatest fear or regret?",
    "What drives you to adventure?"
  ];

  // Debounce timers
  const nameTimer = useRef(null);
  const pronounsTimer = useRef(null);
  const experiencesTimer = useRef(null);
  const descriptionTimer = useRef(null);
  const backgroundTimer = useRef(null);
  const backgroundAnswersTimer = useRef(null);
  const appearanceTimer = useRef(null);
  const appearanceTextTimer = useRef(null);

  // Sync local state when character prop changes (e.g., from loading)
  useEffect(() => {
    setLocalName(character.name || '');
    setLocalPronouns(character.pronouns || '');
    setLocalExperiences(character.experiences || ['', '']);
    setLocalDescription(character.description || '');
    setLocalBackground(character.background || '');
    setLocalBackgroundAnswers(character.backgroundAnswers || {});
    setLocalAppearance(character.appearance || {
      clothes: '',
      eyes: '',
      body: '',
      skin: '',
      attitude: ''
    });
    setLocalAppearanceText(character.appearanceText || '');
  }, [character.id]); // Only sync when character ID changes (new character loaded)

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (nameTimer.current) clearTimeout(nameTimer.current);
      if (pronounsTimer.current) clearTimeout(pronounsTimer.current);
      if (experiencesTimer.current) clearTimeout(experiencesTimer.current);
      if (descriptionTimer.current) clearTimeout(descriptionTimer.current);
      if (backgroundTimer.current) clearTimeout(backgroundTimer.current);
      if (backgroundAnswersTimer.current) clearTimeout(backgroundAnswersTimer.current);
      if (appearanceTimer.current) clearTimeout(appearanceTimer.current);
      if (appearanceTextTimer.current) clearTimeout(appearanceTextTimer.current);
    };
  }, []);

  function handleNameChange(value) {
    setLocalName(value);
    if (nameTimer.current) clearTimeout(nameTimer.current);
    nameTimer.current = setTimeout(() => {
      onUpdate({ name: value });
    }, 500);
  }

  function handlePronounsChange(value) {
    setLocalPronouns(value);
    if (pronounsTimer.current) clearTimeout(pronounsTimer.current);
    pronounsTimer.current = setTimeout(() => {
      onUpdate({ pronouns: value });
    }, 500);
  }

  function handleExperienceChange(index, value) {
    const newExperiences = [...localExperiences];
    newExperiences[index] = value;
    setLocalExperiences(newExperiences);

    if (experiencesTimer.current) clearTimeout(experiencesTimer.current);
    experiencesTimer.current = setTimeout(() => {
      onUpdate({ experiences: newExperiences });
    }, 500);
  }

  function handleDescriptionChange(value) {
    setLocalDescription(value);
    if (descriptionTimer.current) clearTimeout(descriptionTimer.current);
    descriptionTimer.current = setTimeout(() => {
      onUpdate({ description: value });
    }, 500);
  }

  function handleBackgroundChange(value) {
    setLocalBackground(value);
    if (backgroundTimer.current) clearTimeout(backgroundTimer.current);
    backgroundTimer.current = setTimeout(() => {
      onUpdate({ background: value });
    }, 500);
  }

  function handleAppearanceChange(category, value) {
    const newAppearance = {
      ...localAppearance,
      [category]: value
    };
    setLocalAppearance(newAppearance);

    if (appearanceTimer.current) clearTimeout(appearanceTimer.current);
    appearanceTimer.current = setTimeout(() => {
      onUpdate({ appearance: newAppearance });
    }, 500);
  }

  function handleAppearanceTextChange(value) {
    setLocalAppearanceText(value);
    if (appearanceTextTimer.current) clearTimeout(appearanceTextTimer.current);
    appearanceTextTimer.current = setTimeout(() => {
      onUpdate({ appearanceText: value });
    }, 500);
  }

  function handleBackgroundAnswerChange(questionIndex, value) {
    const newAnswers = {
      ...localBackgroundAnswers,
      [`question${questionIndex}`]: value
    };
    setLocalBackgroundAnswers(newAnswers);

    if (backgroundAnswersTimer.current) clearTimeout(backgroundAnswersTimer.current);
    backgroundAnswersTimer.current = setTimeout(() => {
      onUpdate({ backgroundAnswers: newAnswers });
    }, 500);
  }

  // Generate a cohesive description from discrete appearance variables
  function generateAppearanceDescription() {
    const { clothes, eyes, body, skin, attitude } = localAppearance;
    const parts = [];

    if (body) parts.push(`${body} build`);
    if (skin) parts.push(`skin like ${skin}`);
    if (eyes) parts.push(`eyes like ${eyes}`);
    if (clothes) parts.push(`${clothes} clothing`);
    if (attitude) parts.push(`the demeanor of ${attitude}`);

    if (parts.length === 0) return '';

    // Weave into a cohesive sentence
    if (parts.length === 1) {
      return `${character.name || 'This character'} has ${parts[0]}.`;
    } else if (parts.length === 2) {
      return `${character.name || 'This character'} has ${parts[0]} and ${parts[1]}.`;
    } else {
      const lastPart = parts.pop();
      return `${character.name || 'This character'} has ${parts.join(', ')}, and ${lastPart}.`;
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-300 text-small mb-4">
        Add the personal details that make your character unique.
      </p>

      {/* Name */}
      <div>
        <label className="block text-small font-medium text-white mb-2">
          Character Name *
        </label>
        <input
          type="text"
          value={localName}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Enter character name..."
          className="w-full px-4 py-2 bg-surface-2 border border-border-default rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Pronouns */}
      <div>
        <label className="block text-small font-medium text-white mb-2">
          Pronouns
        </label>
        <input
          type="text"
          value={localPronouns}
          onChange={(e) => handlePronounsChange(e.target.value)}
          placeholder="e.g., they/them, she/her, he/him..."
          className="w-full px-4 py-2 bg-surface-2 border border-border-default rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Experiences */}
      <div>
        <label className="block text-small font-medium text-white mb-2">
          Experiences * (2 required)
        </label>
        <p className="text-tiny text-gray-300 mb-3">
          Your character's background experiences that shape who they are.
        </p>
        <div className="space-y-3">
          <input
            type="text"
            value={localExperiences[0] || ''}
            onChange={(e) => handleExperienceChange(0, e.target.value)}
            placeholder="First experience..."
            className="w-full px-4 py-2 bg-surface-2 border border-border-default rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <input
            type="text"
            value={localExperiences[1] || ''}
            onChange={(e) => handleExperienceChange(1, e.target.value)}
            placeholder="Second experience..."
            className="w-full px-4 py-2 bg-surface-2 border border-border-default rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Character Appearance (Discrete Variables) */}
      <div>
        <label className="block text-small font-medium text-white mb-2">
          Character Appearance *
        </label>
        <p className="text-tiny text-gray-300 mb-3">
          Add appearance details for the Portrait Studio.
        </p>

        <DescriptionPromptsHelper
          prompts={descriptionPrompts}
          currentSelections={localAppearance}
          onSelectVariable={handleAppearanceChange}
          combinedValue={localAppearanceText}
          onCombinedChange={handleAppearanceTextChange}
        />
      </div>

      {/* Character Description (Additional Details) */}
      <div>
        <label className="block text-small font-medium text-white mb-2">
          Additional Description
        </label>
        <p className="text-tiny text-gray-300 mb-3">
          Add any additional personality traits, mannerisms, quirks, or other details.
        </p>
        <textarea
          value={localDescription}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder="Additional details about your character..."
          rows={3}
          className="w-full px-4 py-2 bg-surface-2 border border-border-default rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
      </div>

      {/* Background */}
      <div>
        <label className="block text-small font-medium text-white mb-2">
          Background
        </label>
        <p className="text-tiny text-gray-300 mb-3">
          Your character's backstory, history, and narrative details.
        </p>

        <BackgroundQuestionsHelper
          questions={backgroundQuestions}
          currentAnswers={localBackgroundAnswers}
          onAnswerChange={handleBackgroundAnswerChange}
        />

        <textarea
          value={localBackground}
          onChange={(e) => handleBackgroundChange(e.target.value)}
          placeholder="Tell us about your character's story..."
          rows={4}
          className="w-full px-4 py-2 bg-surface-2 border border-border-default rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
      </div>
    </div>
  );
}

export default DetailsSection;
